// src/components/consultorio/OrcamentoModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';

const STATUS_OPTIONS = [
    { value: 'pendente',  label: '⏳ Pendente' },
    { value: 'aprovado',  label: '✅ Aprovado' },
    { value: 'recusado',  label: '❌ Recusado' },
    { value: 'expirado',  label: '⌛ Expirado' },
];

const OrcamentoModal = ({ show, onHide, paciente, orcamento = null, servicos = [], onSaved }) => {
    const { currentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();

    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState({
        titulo:           '',
        observacoes:      '',
        status:           'pendente',
        validade_dias:    30,
        desconto:         0,
        servicos:         [], // [{ servico_id, quantidade, valor_unitario }]
    });

    // Preenche o form ao editar
    useEffect(() => {
        if (orcamento) {
            setForm({
                titulo:        orcamento.titulo || '',
                observacoes:   orcamento.observacoes || '',
                status:        orcamento.status || 'pendente',
                validade_dias: orcamento.validade_dias || 30,
                desconto:      orcamento.desconto || 0,
                servicos:      (orcamento.itens || []).map(i => ({
                    servico_id:    i.servico_id,
                    quantidade:    i.quantidade,
                    valor_unitario: i.valor_unitario,
                    nome:          i.servico?.nome || '',
                })),
            });
        } else {
            setForm({
                titulo: '', observacoes: '', status: 'pendente',
                validade_dias: 30, desconto: 0, servicos: [],
            });
        }
    }, [orcamento, show]);

    const handleAddServico = (opt) => {
        if (!opt) return;
        const servico = servicos.find(s => s.id === opt.value);
        if (!servico) return;
        // Evita duplicata
        if (form.servicos.find(s => s.servico_id === servico.id)) return;
        setForm(prev => ({
            ...prev,
            servicos: [...prev.servicos, {
                servico_id:     servico.id,
                nome:           servico.nome,
                quantidade:     1,
                valor_unitario: parseFloat(servico.valor || 0),
            }],
        }));
    };

    const handleRemoveServico = (servicoId) => {
        setForm(prev => ({
            ...prev,
            servicos: prev.servicos.filter(s => s.servico_id !== servicoId),
        }));
    };

    const handleServicoChange = (servicoId, field, value) => {
        setForm(prev => ({
            ...prev,
            servicos: prev.servicos.map(s =>
                s.servico_id === servicoId ? { ...s, [field]: value } : s
            ),
        }));
    };

    const subtotal = form.servicos.reduce(
        (acc, s) => acc + (parseFloat(s.valor_unitario || 0) * parseInt(s.quantidade || 1)),
        0
    );
    const total = Math.max(0, subtotal - parseFloat(form.desconto || 0));

    const handleSave = async () => {
        if (!form.titulo.trim()) {
            showAlertModal('Atenção', 'Informe um título para o orçamento.', 'warning');
            return;
        }
        if (form.servicos.length === 0) {
            showAlertModal('Atenção', 'Adicione pelo menos um serviço.', 'warning');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...form,
                paciente_id:  paciente.id,
                entidade_id:  currentEntidade.id,
                valor_total:  total,
            };

            let res;
            if (orcamento?.id) {
                res = await axios.put(`/api/orcamentos/${orcamento.id}`, payload);
            } else {
                res = await axios.post('/api/orcamentos', payload);
            }

            onSaved(res.data.orcamento);
            onHide();
        } catch (err) {
            showAlertModal('Erro', err.response?.data?.message || 'Erro ao salvar orçamento.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const inputStyle = {
        width: '100%', padding: '8px 12px', borderRadius: '8px',
        border: '1px solid #e5e7eb', fontSize: 'calc(13px * var(--font-scale))',
        color: '#374151', fontFamily: 'inherit', outline: 'none',
        backgroundColor: '#fff',
    };

    const labelStyle = {
        fontSize: 'calc(11px * var(--font-scale))', fontWeight: '700',
        color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px',
        display: 'block', marginBottom: '5px',
    };

    const servicosOptions = servicos
        .filter(s => s.ativo)
        .map(s => ({ value: s.id, label: `${s.nome} — R$ ${parseFloat(s.valor || 0).toFixed(2)}` }));

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            {/* Header */}
            <Modal.Header style={{ padding: 0, border: 'none' }}>
                <div style={{
                    width: '100%', padding: '20px 24px',
                    background: 'linear-gradient(135deg, #0f172a, #1e40af)',
                    display: 'flex', alignItems: 'center', gap: '12px',
                    position: 'relative',
                }}>
                    <div style={{
                        width: '40px', height: '40px', borderRadius: '10px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>
                            {orcamento ? 'Editar Orçamento' : 'Novo Orçamento'}
                        </div>
                        {paciente?.nome && (
                            <div style={{ fontSize: 'calc(12px * var(--font-scale))', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>
                                {paciente.nome}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onHide}
                        style={{
                            position: 'absolute', top: '50%', right: '16px',
                            transform: 'translateY(-50%)',
                            width: '28px', height: '28px', borderRadius: '6px',
                            border: 'none', background: 'rgba(255,255,255,0.15)',
                            color: '#fff', fontSize: '18px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >×</button>
                </div>
            </Modal.Header>

            {/* Body */}
            <Modal.Body style={{ padding: '20px', backgroundColor: '#f9fafb', maxHeight: '70vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Título + Status */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>Título *</label>
                            <input
                                type="text"
                                placeholder="Ex: Tratamento estético completo"
                                value={form.titulo}
                                onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Status</label>
                            <select
                                value={form.status}
                                onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                                style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
                            >
                                {STATUS_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Validade */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>Validade (dias)</label>
                            <input
                                type="number" min="1" max="365"
                                value={form.validade_dias}
                                onChange={e => setForm(p => ({ ...p, validade_dias: e.target.value }))}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Desconto (R$)</label>
                            <input
                                type="number" min="0" step="0.01"
                                value={form.desconto}
                                onChange={e => setForm(p => ({ ...p, desconto: e.target.value }))}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    {/* Serviços */}
                    <div>
                        <label style={labelStyle}>Adicionar Serviços *</label>
                        <Select
                            options={servicosOptions}
                            onChange={handleAddServico}
                            value={null}
                            placeholder="Buscar e adicionar serviço..."
                            noOptionsMessage={() => 'Nenhum serviço encontrado'}
                            styles={{
                                control: base => ({ ...base, fontSize: 'calc(13px * var(--font-scale))', borderColor: '#e5e7eb', borderRadius: '8px' }),
                                option: base => ({ ...base, fontSize: 'calc(13px * var(--font-scale))' }),
                            }}
                        />
                    </div>

                    {/* Lista de serviços adicionados */}
                    {form.servicos.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {form.servicos.map(s => (
                                <div key={s.servico_id} style={{
                                    padding: '10px 14px', borderRadius: '10px',
                                    backgroundColor: '#fff', border: '1px solid #e5e7eb',
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                }}>
                                    {/* Nome */}
                                    <div style={{ flex: 1, fontSize: 'calc(13px * var(--font-scale))', fontWeight: '500', color: '#374151' }}>
                                        {s.nome}
                                    </div>

                                    {/* Qtd */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <label style={{ fontSize: 'calc(10px * var(--font-scale))', color: '#9ca3af', fontWeight: '600' }}>Qtd</label>
                                        <input
                                            type="number" min="1"
                                            value={s.quantidade}
                                            onChange={e => handleServicoChange(s.servico_id, 'quantidade', e.target.value)}
                                            style={{ ...inputStyle, width: '60px', padding: '5px 8px', textAlign: 'center' }}
                                        />
                                    </div>

                                    {/* Valor unit */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <label style={{ fontSize: 'calc(10px * var(--font-scale))', color: '#9ca3af', fontWeight: '600' }}>R$</label>
                                        <input
                                            type="number" min="0" step="0.01"
                                            value={s.valor_unitario}
                                            onChange={e => handleServicoChange(s.servico_id, 'valor_unitario', e.target.value)}
                                            style={{ ...inputStyle, width: '90px', padding: '5px 8px' }}
                                        />
                                    </div>

                                    {/* Subtotal */}
                                    <div style={{
                                        minWidth: '80px', textAlign: 'right',
                                        fontSize: 'calc(13px * var(--font-scale))', fontWeight: '700', color: '#111827',
                                    }}>
                                        R$ {(parseFloat(s.valor_unitario || 0) * parseInt(s.quantidade || 1)).toFixed(2)}
                                    </div>

                                    {/* Remover */}
                                    <button
                                        onClick={() => handleRemoveServico(s.servico_id)}
                                        style={{
                                            width: '28px', height: '28px', borderRadius: '6px',
                                            border: 'none', backgroundColor: '#fef2f2',
                                            color: '#ef4444', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}

                            {/* Totais */}
                            <div style={{
                                padding: '12px 14px', borderRadius: '10px',
                                backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                                display: 'flex', flexDirection: 'column', gap: '6px',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'calc(12px * var(--font-scale))', color: '#6b7280' }}>
                                    <span>Subtotal:</span>
                                    <span>R$ {subtotal.toFixed(2)}</span>
                                </div>
                                {parseFloat(form.desconto) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'calc(12px * var(--font-scale))', color: '#ef4444' }}>
                                        <span>Desconto:</span>
                                        <span>- R$ {parseFloat(form.desconto).toFixed(2)}</span>
                                    </div>
                                )}
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    fontSize: 'calc(15px * var(--font-scale))', fontWeight: '800', color: '#16a34a',
                                    borderTop: '1px solid #bbf7d0', paddingTop: '6px', marginTop: '2px',
                                }}>
                                    <span>Total:</span>
                                    <span>R$ {total.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Observações */}
                    <div>
                        <label style={labelStyle}>Observações</label>
                        <textarea
                            rows={3}
                            placeholder="Detalhes, condições, validade, etc..."
                            value={form.observacoes}
                            onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))}
                            style={{ ...inputStyle, resize: 'vertical' }}
                        />
                    </div>
                </div>
            </Modal.Body>

            {/* Footer */}
            <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
                <button
                    onClick={onHide}
                    style={{
                        padding: '8px 16px', borderRadius: '8px',
                        border: '1px solid #e5e7eb', backgroundColor: '#fff',
                        color: '#374151', cursor: 'pointer',
                        fontSize: 'calc(13px * var(--font-scale))', fontWeight: '500', fontFamily: 'inherit',
                    }}
                >
                    Cancelar
                </button>
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                        padding: '8px 20px', borderRadius: '8px', border: 'none',
                        background: isSaving ? '#93c5fd' : 'linear-gradient(135deg, #0f172a, #1e40af)',
                        color: '#fff', cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontSize: 'calc(13px * var(--font-scale))', fontWeight: '600', fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: '8px',
                    }}
                >
                    {isSaving && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
                    {isSaving ? 'Salvando...' : (orcamento ? 'Atualizar Orçamento' : 'Criar Orçamento')}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default OrcamentoModal;
